import type { Address } from 'viem'

import { fetchHolding } from '@morpho-org/blue-sdk-viem'
import { useQuery } from '@tanstack/react-query'
import { usePublicClient } from 'wagmi'

/**
 * On-chain counterpart of {@link useAssetCanTransfer} for flows that no longer
 * build a simulation-sdk `SimulationState` (the migrated V1 deposit/withdraw).
 *
 * Reads the connected wallet's holding directly and returns its `canTransfer`
 * flag — `false` only for permissioned assets (e.g. Backed/Coinbase tokens)
 * the wallet isn't authorized to hold/receive. Returns `true` while unknown or
 * loading so it never blocks the action prematurely.
 *
 * `deployless: false` forces the individual-contract-read path in
 * `fetchHolding`, which performs the Backed / permissioned-wrapper reads that
 * populate `canTransfer` (the deployless multicall does not).
 *
 * @param userAddress - The connected wallet (sender on deposit, recipient on withdraw).
 * @param assetAddress - The vault's underlying asset.
 * @param chainId - The chain the asset lives on.
 */
const useAssetCanTransferOnchain = (
  userAddress: Address | undefined,
  assetAddress: Address | undefined,
  chainId: number | undefined
): boolean => {
  const client = usePublicClient({ chainId })

  const { data: holding } = useQuery({
    // `client` is derived from `chainId` via `usePublicClient({ chainId })`, so `chainId`
    // already keys it — it's read from the closure in `queryFn` and kept out of the key.
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: ['asset-can-transfer', chainId, userAddress, assetAddress],
    queryFn: () => fetchHolding(userAddress!, assetAddress!, client!, { deployless: false }),
    enabled: !!userAddress && !!assetAddress && !!client
  })

  return holding?.canTransfer ?? true
}

export default useAssetCanTransferOnchain
